/**
 * SVG-Ausgabe eines {@link ScoreLayout}.
 *
 * Einzige Stelle im Notationsmodul mit DOM-Bezug. Erzeugt ein
 * `<svg>`-Element aus dem rein geometrischen Layout. Farben folgen
 * `currentColor`, sodass die Darstellung dem Farbschema der Seite folgt.
 */

import type { DrumPattern } from '../model/DrumPattern';
import type { LeadsheetMeta } from '../model/leadsheet';
import {
  GEOMETRY,
  layoutPattern,
  type LayoutNote,
  type ScoreLayout,
} from './layout';
import type { RestValue } from './types';

/** Höhe des Kopfbereichs über dem Notensystem (Titel + Tempo). */
const HEADER_HEIGHT = 48;

const SVG_NS = 'http://www.w3.org/2000/svg';

type AttrValue = string | number;

function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, AttrValue> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attrs)) {
    node.setAttribute(key, String(value));
  }
  return node;
}

function line(x1: number, y1: number, x2: number, y2: number, width = 1.4) {
  return el('line', {
    x1,
    y1,
    x2,
    y2,
    stroke: 'currentColor',
    'stroke-width': width,
    'stroke-linecap': 'round',
  });
}

function drawStaff(layout: ScoreLayout): SVGGElement {
  const g = el('g');
  const { geometry } = layout;
  for (const y of geometry.lineYs) {
    g.appendChild(line(geometry.staffLeft, y, geometry.staffRight, y, 1));
  }
  // Anfangs- und Schlussstrich.
  g.appendChild(
    line(
      geometry.staffLeft,
      geometry.topLineY,
      geometry.staffLeft,
      geometry.bottomLineY,
      1.4,
    ),
  );
  g.appendChild(
    line(
      geometry.staffRight,
      geometry.topLineY,
      geometry.staffRight,
      geometry.bottomLineY,
      1.4,
    ),
  );
  return g;
}

function drawClefAndMeter(layout: ScoreLayout): SVGGElement {
  const g = el('g');
  const { geometry } = layout;
  const { gap } = geometry;
  // Schlagzeug-Clef: zwei senkrechte Balken über die mittleren Linien.
  const clefX = geometry.staffLeft + 16;
  const top = geometry.lineYs[1] as number;
  const bottom = geometry.lineYs[3] as number;
  for (const dx of [0, gap * 0.42]) {
    g.appendChild(
      el('rect', {
        x: clefX + dx,
        y: top,
        width: gap * 0.28,
        height: bottom - top,
        fill: 'currentColor',
      }),
    );
  }
  // Taktart 4/4.
  const meterX = clefX + gap * 1.6;
  const numerator = el('text', {
    x: meterX,
    y: (geometry.lineYs[1] as number) + gap * 0.55,
    'font-size': gap * 2.1,
    'font-weight': 700,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    fill: 'currentColor',
  });
  numerator.textContent = '4';
  const denominator = el('text', {
    x: meterX,
    y: (geometry.lineYs[3] as number) + gap * 0.55,
    'font-size': gap * 2.1,
    'font-weight': 700,
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    fill: 'currentColor',
  });
  denominator.textContent = '4';
  g.append(numerator, denominator);
  return g;
}

function drawNoteHead(
  x: number,
  y: number,
  shape: 'normal' | 'cross',
): SVGElement {
  if (shape === 'cross') {
    const s = GEOMETRY.headRx * 0.9;
    const g = el('g');
    g.append(
      line(x - s, y - s, x + s, y + s, 1.8),
      line(x - s, y + s, x + s, y - s, 1.8),
    );
    return g;
  }
  return el('ellipse', {
    cx: x,
    cy: y,
    rx: GEOMETRY.headRx,
    ry: GEOMETRY.headRy,
    fill: 'currentColor',
    transform: `rotate(-18 ${x} ${y})`,
  });
}

function drawFlags(note: LayoutNote): SVGElement {
  const g = el('g');
  const up = note.stemDirection === 'up';
  const step = GEOMETRY.gap * 0.75;
  for (let i = 0; i < note.flags; i += 1) {
    const y = note.stemEndY + (up ? i * step : -i * step);
    const dy = up ? GEOMETRY.gap * 1.4 : -GEOMETRY.gap * 1.4;
    const path = el('path', {
      d: `M ${note.stemX} ${y} q ${GEOMETRY.gap * 1.1} ${dy * 0.25} ${
        GEOMETRY.gap * 0.5
      } ${dy}`,
      stroke: 'currentColor',
      'stroke-width': 1.8,
      fill: 'none',
      'stroke-linecap': 'round',
    });
    g.appendChild(path);
  }
  return g;
}

function drawDot(note: LayoutNote): SVGElement {
  const head = note.heads[0];
  const y = head ? head.y : note.stemStartY;
  return el('circle', {
    cx: note.stemX + GEOMETRY.headRx + 4,
    cy: y,
    r: 1.7,
    fill: 'currentColor',
  });
}

function drawNote(note: LayoutNote): SVGGElement {
  const g = el('g');
  for (const y of note.ledgerYs) {
    const head = note.heads[0];
    const cx = head ? head.x : note.stemX;
    g.appendChild(line(cx - GEOMETRY.gap, y, cx + GEOMETRY.gap, y, 1));
  }
  for (const head of note.heads) {
    g.appendChild(drawNoteHead(head.x, head.y, head.shape));
  }
  g.appendChild(
    line(note.stemX, note.stemStartY, note.stemX, note.stemEndY, 1.6),
  );
  if (note.flags > 0) {
    g.appendChild(drawFlags(note));
  }
  if (note.dotted) {
    g.appendChild(drawDot(note));
  }
  return g;
}

function drawRest(x: number, y: number, value: RestValue): SVGElement {
  const g = el('g');
  const gap = GEOMETRY.gap;
  if (value === 'quarter') {
    const path = el('path', {
      d: `M ${x - gap * 0.35} ${y - gap} L ${x + gap * 0.2} ${y - gap * 0.2} L ${
        x - gap * 0.25
      } ${y + gap * 0.35} L ${x + gap * 0.35} ${y + gap}`,
      stroke: 'currentColor',
      'stroke-width': 2.4,
      fill: 'none',
      'stroke-linejoin': 'round',
    });
    g.appendChild(path);
    return g;
  }
  // Achtel- und Sechzehntelpause: diagonaler Strich mit Fähnchenpunkten.
  const dots = value === 'sixteenth' ? 2 : 1;
  g.appendChild(
    line(x + gap * 0.4, y - gap * 0.8, x - gap * 0.3, y + gap * 0.9, 1.8),
  );
  for (let i = 0; i < dots; i += 1) {
    g.appendChild(
      el('circle', {
        cx: x + gap * 0.4 - i * gap * 0.42,
        cy: y - gap * 0.8 + i * gap * 0.5,
        r: 2,
        fill: 'currentColor',
      }),
    );
  }
  return g;
}

/** Kleines Viertelnoten-Symbol für die Tempoangabe (♩ = NNN). */
function drawTempoMark(x: number, y: number, tempo: number): SVGGElement {
  const g = el('g');
  const cx = x + 4;
  g.append(
    el('ellipse', {
      cx,
      cy: y,
      rx: 3.6,
      ry: 2.8,
      fill: 'currentColor',
      transform: `rotate(-18 ${cx} ${y})`,
    }),
    line(cx + 3, y, cx + 3, y - 13, 1.4),
  );
  const text = el('text', {
    x: x + 14,
    y: y + 4,
    'font-size': 13,
    fill: 'currentColor',
  });
  text.textContent = `= ${tempo}`;
  g.appendChild(text);
  return g;
}

function drawHeader(meta: LeadsheetMeta, geometry: ScoreLayout['geometry']) {
  const g = el('g');
  if (meta.title.length > 0) {
    const title = el('text', {
      x: geometry.staffLeft,
      y: 20,
      'font-size': 17,
      'font-weight': 700,
      fill: 'currentColor',
    });
    title.textContent = meta.title;
    g.appendChild(title);
  }
  g.appendChild(drawTempoMark(geometry.staffLeft, 40, meta.tempo));
  return g;
}

/** Rendert ein Layout samt Kopfzeile in ein neues `<svg>`-Element. */
export function renderScore(
  layout: ScoreLayout,
  meta: LeadsheetMeta,
): SVGSVGElement {
  const { geometry } = layout;
  const totalHeight = geometry.height + HEADER_HEIGHT;
  const svg = el('svg', {
    viewBox: `0 0 ${geometry.width} ${totalHeight}`,
    width: geometry.width,
    height: totalHeight,
    role: 'img',
    'aria-label': 'Notendarstellung des Patterns',
    class: 'score',
  });

  svg.appendChild(drawHeader(meta, geometry));

  // Notensystem und Noten in den Bereich unterhalb der Kopfzeile schieben.
  const body = el('g', { transform: `translate(0 ${HEADER_HEIGHT})` });
  body.appendChild(drawStaff(layout));
  body.appendChild(drawClefAndMeter(layout));

  for (const rest of layout.rests) {
    body.appendChild(drawRest(rest.x, rest.y, rest.value));
  }
  for (const note of layout.notes) {
    body.appendChild(drawNote(note));
  }
  for (const beam of layout.beams) {
    body.appendChild(
      el('rect', {
        x: Math.min(beam.x1, beam.x2),
        y: beam.y - beam.thickness / 2,
        width: Math.abs(beam.x2 - beam.x1),
        height: beam.thickness,
        fill: 'currentColor',
      }),
    );
  }

  for (const tuplet of layout.tuplets) {
    const { x1, x2, y, direction } = tuplet;
    const center = (x1 + x2) / 2;
    const hook = direction === 'up' ? 4 : -4;
    const group = el('g', { class: 'tuplet' });
    group.append(
      line(x1, y + hook, x1, y, 1),
      line(x1, y, center - 7, y, 1),
      line(center + 7, y, x2, y, 1),
      line(x2, y, x2, y + hook, 1),
    );
    const text = el('text', {
      x: center,
      y: y + 4,
      'text-anchor': 'middle',
      'font-size': 13,
      'font-style': 'italic',
      fill: 'currentColor',
    });
    text.textContent = '3';
    group.appendChild(text);
    body.appendChild(group);
  }

  for (const mark of layout.sticking) {
    const text = el('text', {
      x: mark.x,
      y: mark.y,
      'text-anchor': 'middle',
      'font-size': 14,
      'font-family': 'monospace',
      'font-weight': 600,
      fill: 'currentColor',
      class: 'sticking-mark',
    });
    text.textContent = mark.value;
    body.appendChild(text);
  }
  svg.appendChild(body);
  return svg;
}

/** Bequemer Weg von Pattern + Metadaten zur fertigen Notation. */
export function renderScoreFor(
  pattern: DrumPattern,
  meta: LeadsheetMeta,
): SVGSVGElement {
  return renderScore(layoutPattern(pattern), meta);
}
